import { Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import type { SourceShippingAttributes } from '~/types/source-shipping';
import { cn } from '~/utils/cn';
import {
  formatDayRange,
  formatFreeShippingThreshold,
  formatShippingEntry,
  formatSourceShippingField,
} from '~/utils/source-shipping';

/** One rendered row: a label plus one value line, several lines, or nothing (not provided). */
interface ShippingRow {
  id: string;
  label: string;
  lines: string[];
}

/** One titled block of rows (Shipping / Package / Product / Packaging & listing). */
interface ShippingGroup {
  id: string;
  title: string;
  rows: ShippingRow[];
}

interface SourceShippingSectionProps {
  /** Parsed attributes — pass `undefined` (feed states none) and nothing renders. */
  attributes: SourceShippingAttributes | undefined;
  /** Drops the card chrome when the section is embedded inside another card / modal. */
  bare?: boolean;
  className?: string;
  /** Automation hook: section id; groups/rows get `${testId}-group-{id}` / `${testId}-row-{id}`. */
  testId?: string;
}

/**
 * Read-only "Shipping & logistics" block for a GMC product: the merchant feed's
 * own shipping rules, dimensions and packaging (NOT enrichment output, hence the
 * "From merchant feed" badge). Groups where the feed states nothing are hidden;
 * inside a shown group a missing value reads "Not provided", never zero.
 */
export function SourceShippingSection({
  attributes,
  bare,
  className,
  testId = 'source-shipping-section',
}: SourceShippingSectionProps) {
  const { t } = useTranslation('common');
  if (!attributes) return null;

  const single = (key: keyof SourceShippingAttributes): ShippingRow => {
    const value = formatSourceShippingField(attributes, key, t);
    return { id: key, label: t(`sourceShipping.fields.${key}`), lines: value ? [value] : [] };
  };

  const handling = formatDayRange(attributes.minHandlingTime, attributes.maxHandlingTime, t);
  const groups: ShippingGroup[] = [
    {
      id: 'shipping',
      title: t('sourceShipping.groups.shipping'),
      rows: [
        {
          id: 'shipping',
          label: t('sourceShipping.fields.shipping'),
          lines: (attributes.shipping ?? []).map((entry) => formatShippingEntry(entry, t)),
        },
        {
          id: 'freeShippingThreshold',
          label: t('sourceShipping.fields.freeShippingThreshold'),
          lines: (attributes.freeShippingThreshold ?? []).map((th) =>
            formatFreeShippingThreshold(th, t),
          ),
        },
        single('shippingLabel'),
        single('transitTimeLabel'),
        {
          id: 'handlingTime',
          label: t('sourceShipping.fields.handlingTime'),
          lines: handling ? [handling] : [],
        },
      ],
    },
    {
      id: 'package',
      title: t('sourceShipping.groups.package'),
      rows: [
        single('shippingWeight'),
        single('shippingLength'),
        single('shippingWidth'),
        single('shippingHeight'),
      ],
    },
    {
      id: 'product',
      title: t('sourceShipping.groups.product'),
      rows: [
        single('productWeight'),
        single('productLength'),
        single('productWidth'),
        single('productHeight'),
      ],
    },
    {
      id: 'listing',
      title: t('sourceShipping.groups.listing'),
      rows: [single('isBundle'), single('multipack'), single('shortTitle')],
    },
  ];
  // A group with nothing stated is noise — most feeds set only a few of the 17.
  const visibleGroups = groups.filter((g) => g.rows.some((r) => r.lines.length > 0));
  if (visibleGroups.length === 0) return null;

  return (
    <section
      data-testid={testId}
      className={cn(
        !bare && 'overflow-hidden rounded-2xl border bg-white p-5 shadow-sm',
        className,
      )}
    >
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Truck className="text-muted-foreground size-4" aria-hidden />
        <h2 className="text-base font-semibold" data-testid={`${testId}-title`}>
          {t('sourceShipping.title')}
        </h2>
        <Badge variant="outline" className="font-medium" data-testid={`${testId}-feed-badge`}>
          {t('sourceShipping.fromFeed')}
        </Badge>
      </div>
      <p className="text-muted-foreground mb-4 text-sm leading-5">{t('sourceShipping.subtitle')}</p>
      <div className="flex flex-col gap-5">
        {visibleGroups.map((group) => (
          <div key={group.id} data-testid={`${testId}-group-${group.id}`}>
            <h3 className="text-foreground mb-2 text-sm font-semibold">{group.title}</h3>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
              {group.rows.map((row) => (
                <div
                  key={row.id}
                  data-testid={`${testId}-row-${row.id}`}
                  className={cn(
                    'flex justify-between gap-4 border-b py-1.5',
                    // Rule lists run long — give them the full row width.
                    row.lines.length > 1 && 'sm:col-span-2',
                  )}
                >
                  <dt className="text-muted-foreground shrink-0 text-sm">{row.label}</dt>
                  <dd
                    className="text-foreground min-w-0 text-right text-sm break-words"
                    data-testid={`${testId}-row-${row.id}-value`}
                  >
                    {row.lines.length === 0 ? (
                      <span className="text-muted-foreground italic">
                        {t('sourceShipping.notProvided')}
                      </span>
                    ) : row.lines.length === 1 ? (
                      row.lines[0]
                    ) : (
                      <ul className="flex flex-col gap-0.5">
                        {row.lines.map((line, i) => (
                          <li key={i}>{line}</li>
                        ))}
                      </ul>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </section>
  );
}
